import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {}
      if (attempts.current < 20) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center p-6 grid-bg">
      <div className="max-w-lg w-full border border-white/10 bg-[#121215] p-10 relative" data-testid="payment-success-card">
        <div className="beam-border absolute inset-0 pointer-events-none" />
        <div className="relative z-10">
          {status === "checking" && (
            <>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-4">Vérification…</div>
              <h1 className="font-display text-3xl font-bold mb-4">Confirmation en cours</h1>
              <p className="text-white/60">Nous confirmons votre paiement avec Stripe. Cela peut prendre quelques secondes…</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-10 h-10 text-[#FFD60A] mb-6" strokeWidth={1.5} />
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FFD60A] mb-4">Paiement confirmé</div>
              <h1 className="font-display text-4xl font-black tracking-tight mb-4">Bienvenue Pro.</h1>
              <p className="text-white/70 mb-8">
                Votre abonnement SEO Veille est actif. Vous avez maintenant accès à toute la veille, la tendance quotidienne et les archives.
              </p>
              <Link
                to="/dashboard"
                data-testid="payment-success-continue-btn"
                className="inline-flex items-center gap-3 bg-[#FF453A] text-black px-6 py-3 font-mono text-sm uppercase tracking-widest hover:bg-white transition-colors"
              >
                Accéder au dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
          {status === "timeout" && (
            <>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF453A] mb-4">Délai dépassé</div>
              <h1 className="font-display text-3xl font-bold mb-4">Le paiement est encore en traitement</h1>
              <p className="text-white/60 mb-6">
                Vérifiez à nouveau dans quelques instants. Si vous êtes débité, votre plan sera activé automatiquement.
              </p>
              <Link
                to="/dashboard"
                data-testid="payment-timeout-continue-btn"
                className="inline-flex items-center gap-3 bg-white text-black px-6 py-3 font-mono text-sm uppercase tracking-widest"
              >
                Retour au dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
          {status === "failed" && (
            <>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF453A] mb-4">Erreur</div>
              <h1 className="font-display text-3xl font-bold mb-4">Impossible de confirmer</h1>
              <Link to="/dashboard" className="text-[#FF453A] underline underline-offset-4">Retour au dashboard</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
